import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logoSrc from '../../../assets/logo.svg';
import { servicesData } from '../../../data/services';
// 미디어 센터 출시 전까지 메가 메뉴 비활성화 — blogSections import 보류
// import { blogSections } from '../../../data/blogPosts';
import { useEmergencyMode } from '../../../features/Chatbot/useEmergencyMode';
import styles from './Nav.module.css';

function EmergencyLinkContent({ emergency }) {
  return (
    <>
      <svg
        className={styles.urgentIcon}
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M12 3v1.6M5 6l1.1 1.1M19 6l-1.1 1.1"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path d="M6.5 14a5.5 5.5 0 0 1 11 0v.5h-11v-.5Z" fill="currentColor" />
        <rect x="5.5" y="14.5" width="13" height="2.4" rx="0.6" fill="currentColor" />
      </svg>
      <span>긴급 상담</span>
      <span
        className={`${styles.urgentDot} ${emergency ? styles.urgentDotActive : ''}`}
        aria-hidden="true"
      />
    </>
  );
}

export function Nav() {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const isHome = pathname === '/';
  const emergency = useEmergencyMode();
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);

  // 타이머 refs — 드롭다운과 버튼 사이 갭에서 즉시 닫히는 것 방지
  const svcCloseTimer = useRef(null);

  function openServices() {
    clearTimeout(svcCloseTimer.current);
    setServicesOpen(true);
  }
  function scheduleCloseServices() {
    svcCloseTimer.current = setTimeout(() => setServicesOpen(false), 120);
  }

  function scrollToSection(id) {
    if (isHome) {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(`/#${id}`);
    }
  }

  // Escape 키로 닫기
  useEffect(() => {
    if (!servicesOpen) return;
    function handleKey(e) {
      if (e.key === 'Escape') {
        setServicesOpen(false);
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [servicesOpen]);

  // 모바일: 라우트 변경 시 메뉴 닫기 + 스크롤 잠금
  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect */
    setMobileOpen(false);
    setMobileServicesOpen(false);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [pathname, hash]);

  useEffect(() => {
    if (!mobileOpen) return;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => e.key === 'Escape' && setMobileOpen(false);
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [mobileOpen]);

  const handleMobileSection = (id) => {
    setMobileOpen(false);
    setTimeout(() => scrollToSection(id), 60);
  };

  return (
    <nav className={styles.nav}>
      <div className={styles.inner}>
        <Link
          to="/"
          className={styles.logo}
          onClick={(e) => {
            // 이미 홈이면 React Router 가 아무 동작도 안 함 → 맨 위로 스크롤
            if (isHome) {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }}
        >
          <img src={logoSrc} alt="mediLawyer" className={styles.logoImg} />
        </Link>

        {/* Desktop links */}
        <div className={styles.links}>
          {/* 서비스 */}
          <div
            className={styles.dropdownWrap}
            onMouseEnter={openServices}
            onMouseLeave={scheduleCloseServices}
          >
            <button
              className={styles.link}
              onClick={() => { scrollToSection('services'); setServicesOpen(false); }}
              onFocus={openServices}
              aria-haspopup="menu"
              aria-expanded={servicesOpen}
            >
              서비스
              <svg
                className={`${styles.caret} ${servicesOpen ? styles.caretOpen : ''}`}
                width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"
              >
                <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.5"
                  strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          <Link to="/network" className={styles.link}>네트워크</Link>

          {/* 미디어 센터 — 출시 전까지 단순 링크 (ComingSoon 페이지로 이동) */}
          <Link to="/blog" className={styles.link}>미디어 센터</Link>

          <Link to="/contract" className={styles.link}>계약 안내</Link>
          <Link
            to="/chat"
            className={`${styles.link} ${styles.urgent} ${emergency ? styles.urgentOn : ''}`}
          >
            <EmergencyLinkContent emergency={emergency} />
          </Link>
          <button className={styles.cta} onClick={() => scrollToSection('inquiry')}>
            상담 신청
          </button>
        </div>

        {/* Mobile: hamburger */}
        <button
          type="button"
          className={styles.hamburger}
          onClick={() => setMobileOpen((o) => !o)}
          aria-label={mobileOpen ? '메뉴 닫기' : '메뉴 열기'}
          aria-expanded={mobileOpen}
          aria-controls="mobile-menu"
        >
          <span className={`${styles.hamburgerBar} ${mobileOpen ? styles.hamburgerBarTop : ''}`} />
          <span className={`${styles.hamburgerBar} ${mobileOpen ? styles.hamburgerBarMid : ''}`} />
          <span className={`${styles.hamburgerBar} ${mobileOpen ? styles.hamburgerBarBot : ''}`} />
        </button>
      </div>

      {/* 서비스 메가 메뉴 */}
      {servicesOpen && (
        <div
          className={styles.megaMenu}
          role="menu"
          onMouseEnter={openServices}
          onMouseLeave={scheduleCloseServices}
        >
          <div className={styles.megaInner}>
            <div className={styles.megaServicesGrid}>
              {servicesData.map((svc) => (
                <Link
                  key={svc.slug}
                  to={`/services/${svc.slug}`}
                  className={styles.megaServiceCard}
                  role="menuitem"
                  onClick={() => setServicesOpen(false)}
                >
                  <span className={styles.megaCat}>{svc.cat}</span>
                  <span className={styles.megaTitle}>{svc.title.replace(/\n/g, ' ')}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 미디어센터 메가 메뉴 — 출시 전까지 비활성화 */}

      {/* Mobile menu drawer */}
      <div
        id="mobile-menu"
        className={`${styles.mobileMenu} ${mobileOpen ? styles.mobileMenuOpen : ''}`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!mobileOpen}
      >
        <div className={styles.mobileInner}>
          {/* 서비스 expandable */}
          <button
            type="button"
            className={styles.mobileItem}
            onClick={() => setMobileServicesOpen((o) => !o)}
            aria-expanded={mobileServicesOpen}
          >
            <span>서비스</span>
            <svg
              className={`${styles.mobileCaret} ${mobileServicesOpen ? styles.mobileCaretOpen : ''}`}
              width="14" height="14" viewBox="0 0 10 10" fill="none" aria-hidden="true"
            >
              <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.5"
                strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {mobileServicesOpen && (
            <div className={styles.mobileSubList}>
              <button
                type="button"
                className={styles.mobileSubItemAll}
                onClick={() => handleMobileSection('services')}
              >
                전체 서비스 보기
              </button>
              {servicesData.map((svc) => (
                <Link key={svc.slug} to={`/services/${svc.slug}`} className={styles.mobileSubItem}>
                  <span className={styles.mobileSubCat}>{svc.cat}</span>
                  <span className={styles.mobileSubTitle}>{svc.title.replace(/\n/g, ' ')}</span>
                </Link>
              ))}
            </div>
          )}

          <Link to="/network" className={styles.mobileItem}>네트워크</Link>

          {/* 미디어 센터 — 출시 전까지 단순 링크 */}
          <Link to="/blog" className={styles.mobileItem}>미디어 센터</Link>

          <Link to="/contract" className={styles.mobileItem}>계약 안내</Link>
          <Link
            to="/chat"
            className={`${styles.mobileItem} ${styles.mobileUrgent} ${emergency ? styles.mobileUrgentOn : ''}`}
          >
            <EmergencyLinkContent emergency={emergency} />
          </Link>

          <button
            type="button"
            className={styles.mobileCta}
            onClick={() => handleMobileSection('inquiry')}
          >
            상담 신청
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5"
                strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>

      {/* Backdrop */}
      {mobileOpen && (
        <div
          className={styles.mobileBackdrop}
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}
    </nav>
  );
}
